import { cloneDeep, isEqual } from 'lodash';
import { v1 as uuid } from 'uuid';

import { postQuery } from './helper';

// In PSP mode, pspEntry writes the cached bearer token here so that
// connection_init can pass it for WebSocket auth (cookies don't flow through
// the WS upgrade request in PSP mode — only the bearer token works).
const getPspBearerToken = (): string | null =>
  (window as unknown as { __OG_STORIES_PSP_BEARER__?: string }).__OG_STORIES_PSP_BEARER__ ?? null;

export type SocketCallback = (event: any) => void;
export type LongPollCallback = (payload: any) => void;
export type MessageListener = (messageData: any) => void;
export type ListenerUuid = string;

export interface ISubscribePayload {
  variables: any;
}

export interface IHasuraSubscriptionParams {
  query: string;
  longPollInterval?: number;
}

export const LONG_POLL_INTERVAL_DEFAULT = 5000; // 5 sec
export const LONG_POLL_INTERVAL_SHORT = 3000; // 3 sec

export const getBaseUrl = (): string => {
  const { protocol, host } = window.location;
  return `${protocol === 'https:' ? 'wss:' : 'ws:'}//${host}`;
};

// Web Socket / Long Poll connection with Hasura to handle graphql subscriptions
export class HasuraSubscription {
  query: string;
  variables: any;

  socket: WebSocket;
  socketListeners: Map<string, SocketCallback>;

  longPoll: any;
  longPollInterval: number;
  longPollLatestData: any;
  longPollListeners: Map<string, LongPollCallback>;

  constructor(params: IHasuraSubscriptionParams) {
    this.query = params.query;
    this.longPollInterval = params.longPollInterval || LONG_POLL_INTERVAL_DEFAULT;
    this.socketListeners = new Map();
    this.longPollListeners = new Map();
  }

  subscribe(payload: ISubscribePayload) {
    this.variables = payload.variables;
    if (!this.socket) {
      this.initWebSocket();
    } else {
      switch (this.socket.readyState) {
        case 0: // CONNECTING — initWebSocket()'s open listener will call setupSubscription()
          break;
        case 1: // OPEN — send start immediately
          this.setupSubscription();
          break;
        default: // CLOSING or CLOSED — fall back to long poll
          this.initLongPolling();
      }
    }
  }

  unsubscribe() {
    this.variables = null;
    clearInterval(this.longPoll);
    this.clearAllMessageListeners();
  }

  addMessageListener(messageListener: MessageListener): ListenerUuid {
    const listenerUuid = uuid();
    this.addSocketMessageListener(listenerUuid, messageListener);
    this.addLongPollMessageListener(listenerUuid, messageListener);
    return listenerUuid;
  }

  removeMessageListener(listenerUuid: ListenerUuid) {
    this.removeSocketMessageListener(listenerUuid);
    this.removeLongPollMessageListener(listenerUuid);
  }

  clearAllMessageListeners() {
    this.clearSocketMessageListeners();
    this.clearLongPollMessageListeners();
  }

  private initWebSocket() {
    this.socket = new WebSocket(`${getBaseUrl()}/api/data/objects/stories/v1/graphql`, 'graphql-ws');
    this.socket.addEventListener('open', () => {
      const bearerToken = getPspBearerToken();
      const authHeaders = bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {};
      this.socket.send(
        JSON.stringify({
          type: 'connection_init',
          payload: {
            headers: { 'content-type': 'application/json', ...authHeaders },
            lazy: true
          }
        })
      );
      // add/refresh listeners if they were added before connection open
      this.socketListeners.forEach((socketListener) => {
        this.socket.removeEventListener('message', socketListener);
        this.socket.addEventListener('message', socketListener);
      });
      this.setupSubscription();
    });
    this.socket.addEventListener('close', () => {
      this.initLongPolling();
    });
  }

  private setupSubscription() {
    this.socket.send(
      JSON.stringify({
        id: uuid(),
        type: 'start',
        payload: {
          query: this.query,
          variables: this.variables
        }
      })
    );
  }

  private addSocketMessageListener(listenerUuid: ListenerUuid, messageListener: MessageListener) {
    const onWSMessageReceived = (event) => {
      const parsedMessage: any = JSON.parse(event.data);
      if (parsedMessage?.type === 'data') {
        messageListener(parsedMessage.payload);
      }
    };
    this.socketListeners.set(listenerUuid, onWSMessageReceived);
    if (this.socket) this.socket.addEventListener('message', onWSMessageReceived);
  }

  private removeSocketMessageListener(listenerUuid: ListenerUuid) {
    const socketListener = this.socketListeners.get(listenerUuid);
    if (socketListener) {
      if (this.socket) this.socket.removeEventListener('message', socketListener);
      this.socketListeners.delete(listenerUuid);
    }
  }

  private clearSocketMessageListeners() {
    if (this.socket) {
      this.socketListeners.forEach((socketListener) => {
        this.socket.removeEventListener('message', socketListener);
      });
    }
    this.socketListeners.clear();
  }

  private initLongPolling() {
    clearInterval(this.longPoll);
    if (!this.variables) return;
    const variables = cloneDeep(this.variables);
    const longPollHandler = () => {
      void postQuery({
        query: this.query.replace('subscription', 'query'),
        variables: this.variables || {}
      }).then((data) => {
        if (isEqual(this.variables, variables) && !isEqual(this.longPollLatestData, data)) {
          this.longPollLatestData = data;
          this.longPollListeners.forEach((messageListener) => {
            messageListener(data);
          });
        }
      });
    };
    this.longPoll = setInterval(longPollHandler, this.longPollInterval);
    longPollHandler();
  }

  private addLongPollMessageListener(listenerUuid: ListenerUuid, messageListener: MessageListener): void {
    this.longPollListeners.set(listenerUuid, messageListener);
  }

  private removeLongPollMessageListener(listenerUuid: ListenerUuid): void {
    this.longPollListeners.delete(listenerUuid);
  }

  private clearLongPollMessageListeners() {
    this.longPollListeners.clear();
    this.longPollLatestData = null;
  }
}
